import { z } from "zod";

// wills-intestacy — "What happens to your estate if you die without a will",
// per state (NSW / VIC / QLD MVP), plus an estate-readiness signal.
//
// Architecture rule #1: rules are *data*, versioned per state + effective date,
// kept in git, unit-tested against each state's own succession Act / plain-
// language pages (the moat). Calculation is pure: `inputs -> rules -> outputs`.
// No I/O, no `Date.now()` — the date of death / assessment date is ALWAYS a
// parameter so a backdated estate resolves against the statutory legacy in force
// on that date (NSW reprices quarterly; VIC indexes 1 July annually).
//
// REGULATORY (binding — panels/intestacy-checker.md §2):
//   * This is GENERAL INFORMATION about the statutory intestacy default, NOT
//     legal advice and NOT financial-product advice (a will is a legal service,
//     not an `s764A` financial product — no AFSL line). The copy layer carries
//     the "intestacy default, not legal advice — see a solicitor / your state
//     trustee" caveat and the mandatory "Educational tool. Not financial
//     advice." disclaimer.
//   * The ruleset answers "who inherits your ESTATE under intestacy" and STOPS.
//     Super death benefits, testamentary trusts and life-insurance proceeds are
//     `s764A`/financial-product-adjacent and are NEVER modelled here (regulatory
//     HIGH). The guard `assertEstateOnly` in calculate.ts hard-enforces that no
//     such identifier rides a distribution surface.
//   * NO affiliate / will-provider identifier may reach this module — the rules
//     emit FACTS ONLY. The handoff (Safewill etc.) lives off the calc surface,
//     in app/marketing. `assertNoAffiliate` enforces it.

/** The three states shipped in the MVP (~70% of population). WA/SA/ACT/TAS/NT staged for v2. */
export const INTESTACY_STATES = ["NSW", "VIC", "QLD"] as const;
export type IntestacyState = (typeof INTESTACY_STATES)[number];

/**
 * Surviving-partner status. A current spouse OR a recognised de facto partner is
 * a "partner" for intestacy in all three states; "none" covers single, widowed,
 * and fully-divorced. (Separated-but-not-divorced is still a spouse at law — the
 * UI should map that to "partner"; we don't model the contested de-facto
 * recognition test here, which is a court question, not a deterministic rule.)
 */
export const PARTNER_STATUS = ["partner", "none"] as const;
export type PartnerStatus = (typeof PARTNER_STATUS)[number];

/**
 * Children classification — the load-bearing intestacy distinction.
 *  - "none": no surviving children/issue.
 *  - "all-shared": all surviving children are children of the surviving partner
 *    too (the "blended family" surprise does NOT apply).
 *  - "some-other": at least one surviving child is from a relationship OTHER
 *    than with the surviving partner (step-family / prior relationship) — this
 *    is the case the providers bury and users guess wrong.
 *
 * When there is no partner, "all-shared" and "some-other" both just mean "has
 * issue" (the children take the whole estate either way).
 */
export const CHILDREN_STATUS = ["none", "all-shared", "some-other"] as const;
export type ChildrenStatus = (typeof CHILDREN_STATUS)[number];

/** Next-of-kin available when there is no partner and no issue. */
export const KIN_STATUS = ["parents", "siblings", "none"] as const;
export type KinStatus = (typeof KIN_STATUS)[number];

/**
 * One dated statutory-legacy value for a state. The legacy is the fixed sum the
 * partner takes off the top before any split with non-partner children. Each
 * value is effective-dated and NEVER mutated once shipped — a new period gets a
 * new entry (NSW reprices quarterly to CPI; VIC indexes each 1 July).
 */
export interface StatutoryLegacyPeriod {
  /** Inclusive ISO start date this value is in force from. */
  effectiveFrom: string;
  /** Inclusive ISO end date, or null if open-ended/current. */
  effectiveTo: string | null;
  /** The statutory legacy / prescribed amount in whole dollars (cents kept where the source carries them). */
  amount: number;
  /** Source URL the value was transcribed from. */
  source: string;
}

/** Versioned intestacy ruleset for one state. */
export interface IntestacyRuleSet {
  state: IntestacyState;
  /** Plain name of the governing Act, for citation. */
  act: string;
  /** Section(s) that define the partner/issue distribution, for citation. */
  distributionRef: string;
  /** Source-of-truth URLs the rules were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /**
   * Statutory-legacy schedule, newest period first. The partner's fixed sum off
   * the top when there are non-partner children. NSW/VIC carry a real indexed
   * legacy; QLD's "statutory legacy" is the fixed $150,000 "prescribed amount"
   * (not indexed) — modelled as a single open-ended period.
   */
  statutoryLegacy: readonly StatutoryLegacyPeriod[];
  /**
   * The partner's fraction of the residue (after personal effects + statutory
   * legacy) when there are non-partner children. NSW/VIC: 1/2. QLD splits this
   * by child count, so this is the DEFAULT and `partnerResidueShareOneChild`
   * overrides it for the single-child case.
   */
  partnerResidueShare: number;
  /**
   * QLD only: the partner's residue fraction when there is exactly ONE child
   * (Succession Act 1981 Sch 2 Pt 1: 1/2 with one child, 1/3 with more than one).
   * `null` for states where the share doesn't depend on child count.
   */
  partnerResidueShareOneChild: number | null;
  /**
   * Whether, when the partner is the parent of ALL the surviving children, the
   * partner takes the WHOLE estate (true for NSW s112 and VIC; QLD instead
   * always applies the chattels + amount + residue-fraction split, even for
   * shared children).
   */
  partnerTakesAllWhenAllShared: boolean;
}

// ---------------------------------------------------------------------------
// intestacyDistribution
// ---------------------------------------------------------------------------

export const IntestacyInputSchema = z.object({
  state: z.enum(INTESTACY_STATES),
  /** Surviving-partner status. */
  partner: z.enum(PARTNER_STATUS),
  /** Children classification (the load-bearing field). */
  children: z.enum(CHILDREN_STATUS),
  /**
   * Number of surviving children — only used to pick QLD's 1/2-vs-1/3 residue
   * split and to share a children's pool equally. Defaults sensibly from
   * `children` (>=1 when children !== "none").
   */
  childCount: z.number().int().min(0).optional(),
  /** Next-of-kin available, used only when partner === "none" && children === "none". */
  kin: z.enum(KIN_STATUS).default("none"),
  /**
   * Net estate value in whole dollars, optional. When supplied we resolve the
   * actual dollar shares and detect the "estate below the statutory legacy →
   * partner takes all" case. When omitted we return shares as fractions/labels
   * only (no invented dollar figure).
   */
  estateValue: z.number().min(0).optional(),
  /**
   * The date of death / assessment date (ISO YYYY-MM-DD). Passed in (never the
   * clock) so the right effective-dated statutory legacy is chosen.
   */
  assessmentDate: z.string(),
});
/** Caller-facing input — defaulted fields optional (z.input). */
export type IntestacyInput = z.input<typeof IntestacyInputSchema>;

/** Who a share goes to. */
export const BENEFICIARY = [
  "partner",
  "children",
  "parents",
  "siblings",
  "crown",
] as const;
export type Beneficiary = (typeof BENEFICIARY)[number];

/** One line of the statutory distribution. */
export interface DistributionShare {
  /** Recipient class. */
  to: Beneficiary;
  /**
   * What this recipient receives, as cited components:
   *  - personalEffects: takes the deceased's personal effects/household chattels.
   *  - statutoryLegacy: the fixed indexed sum (dollars), 0 when not applicable.
   *  - residueFraction: fraction of the residue (after legacy) this recipient takes.
   */
  personalEffects: boolean;
  statutoryLegacy: number;
  residueFraction: number;
  /** Resolved dollar amount for this recipient, when `estateValue` was supplied. */
  amount: number | null;
}

export interface IntestacyOutput {
  state: IntestacyState;
  /** The Act + section the distribution is cited to. */
  act: string;
  distributionRef: string;
  /** The statutory legacy value in force on the assessment date (0 if not used). */
  statutoryLegacy: number;
  /** ISO effective-from of the statutory-legacy period used (for inline dating). */
  statutoryLegacyEffectiveFrom: string | null;
  /** The ordered distribution lines. */
  shares: readonly DistributionShare[];
  /**
   * True when the partner takes the WHOLE estate (either no issue / all-shared
   * "partner takes all" states, or the estate is below the statutory legacy so
   * the split never bites). The single most surprise-correcting flag.
   */
  partnerTakesAll: boolean;
  /**
   * True when there ARE non-partner children and the estate exceeds the legacy,
   * so the partner does NOT take all — the blended-family surprise. Drives the
   * lead copy.
   */
  blendedSplitApplies: boolean;
  /** True when nobody qualifies and the estate passes to the Crown (bona vacantia). */
  passesToCrown: boolean;
}

// ---------------------------------------------------------------------------
// estateReadiness
// ---------------------------------------------------------------------------

/** The four documents that fall over at death or incapacity. */
export const READINESS_DOCS = [
  "will",
  "enduringPowerOfAttorney",
  "enduringGuardianship",
  "guardianNomination",
] as const;
export type ReadinessDoc = (typeof READINESS_DOCS)[number];

/** Per-item readiness status — deliberately NOT a "score out of 100". */
export const READINESS_STATUS = ["covered", "stale", "gap", "na"] as const;
export type ReadinessStatus = (typeof READINESS_STATUS)[number];

export const EstateReadinessInputSchema = z.object({
  /** Do you have a will at all? */
  hasWill: z.boolean(),
  /**
   * Has a life event (marriage, divorce, new child) happened SINCE the will was
   * made? Marriage/divorce can revoke or partially invalidate a will in several
   * states, so a "yes" flags the existing will as stale. Ignored when !hasWill.
   */
  lifeEventSinceWill: z.boolean().default(false),
  /** Do you have an enduring power of attorney (financial/legal)? */
  hasEnduringPowerOfAttorney: z.boolean(),
  /** Do you have an enduring guardianship / medical decision-maker? */
  hasEnduringGuardianship: z.boolean(),
  /** Do you have minor (dependent) children? Drives whether guardian-nomination applies. */
  hasMinorChildren: z.boolean(),
  /** Have you nominated a guardian for your minor children (only relevant if hasMinorChildren)? */
  hasGuardianNomination: z.boolean().default(false),
});
/** Caller-facing input — defaulted fields optional (z.input). */
export type EstateReadinessInput = z.input<typeof EstateReadinessInputSchema>;

export interface ReadinessItem {
  doc: ReadinessDoc;
  status: ReadinessStatus;
  /** The factual state default if this document is missing (information, not advice). */
  defaultIfMissing: string;
}

export interface EstateReadinessOutput {
  items: readonly ReadinessItem[];
  /** Count of items with a "gap" status (missing where it applies). */
  gaps: number;
  /** Count of items flagged "stale" (present but possibly affected by a life event). */
  stale: number;
  /** Count of items "covered". */
  covered: number;
  /** Count of applicable items (covered + stale + gap; excludes "na"). */
  applicable: number;
  /**
   * Whether anything needs attention (any gap or stale item). A boolean prompt
   * to ACT — never a graded product recommendation or a "you should buy X".
   */
  actionSuggested: boolean;
}
